import * as vp from 'vopay';
import React, { FunctionComponent, useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import usePostCreateLead from '../../../hooks/useApi/usePostCreateLead';
import LeadCreationForm from './LeadCreationForm';
import LeadCreationContext, { LeadCreationCtx } from './LeadCreationContext';
import LeadCreationDialogue from './LeadCreationDialogue';
import { LeadCreationDialogueHandlers, DialogueHandlerProps } from '../types';
import { usePartnerLogin } from '../../../providers/PartnerLogin.provider';
import { useTranslation } from 'react-i18next';

const { Box, BackButton, SubTitle } = vp;

type FC = FunctionComponent;

const LeadCreationContainer: FC = () => {
  const reviewRef = React.createRef<HTMLDivElement>();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const from = decodeURIComponent(queryParams.get('from') || '');
  const history = useHistory();
  const [leadCreateError, setLeadCreateError] = useState('');
  const [showLeadCreateErrorAlert, setShowLeadCreateErrorAlert] = useState(false);
  const [showLeadCreateSuccessAlert, setShowLeadCreateSuccessAlert] = useState(false);
  const [shouldCreateLead, setShouldCreateLead] = useState(false);
  const [createLeadResponse, createCreateLead] = usePostCreateLead({});
  const [reviewing, setReviewing] = useState(false);
  const { t } = useTranslation('translations', { keyPrefix: 'Leads.Create' });
  const [defaultValues, setDefaultValues] = useState<vp.CreateLeadRequest>({
    BusinessEmail: '',
    CompanyName: '',
    DBA: '',
    websiteURL: '',
    businessModel: '',
    Comments: '',
    FirstName: '',
    LastName: '',
    PartnerID: '',
    AgentID: '',
    SystemOfRecord: '',
    DataAggregator: '',
  });
  const {
    response: { accountId, loginUsername, isInsideSales },
  } = usePartnerLogin();

  const leadCreation: LeadCreationCtx = {
    showLeadCreateErrorAlert,
    showLeadCreateSuccessAlert,
    leadCreateError,
  };

  const handlers = {
    handleDialogue: (handlerProps: DialogueHandlerProps<LeadCreationDialogueHandlers>) => {
      const { type, state, action = 'default' } = handlerProps;

      const dialogueHandlers: LeadCreationDialogueHandlers = {
        create: {
          success: {
            default: () => {
              setShowLeadCreateSuccessAlert(false);
              history.push('/leads');
            },
          },
          fail: {
            default: () => {
              setShowLeadCreateErrorAlert(false);
              setLeadCreateError('');
            },
          },
        },
      };
      const dialogueState = dialogueHandlers[type][state];
      const handler = dialogueState[action] || dialogueState.default;

      handler();
    },
    onCreateLead: (values: vp.CreateLeadRequest) => {
      setShouldCreateLead(true);
      const lead = { ...values };
      if (!isInsideSales) {
        lead.PartnerID = accountId;
        lead.AgentID = loginUsername;
      }
      createCreateLead(lead);
    },
    onReview: (values: vp.CreateLeadRequest) => {
      setDefaultValues(values);
      setReviewing(true);
    },
  };
  useEffect(() => {
    if (shouldCreateLead === true) {
      const { Success, ErrorMessage } = createLeadResponse;

      if (Success === true) {
        setShowLeadCreateSuccessAlert(true);
      } else {
        setLeadCreateError(ErrorMessage);
        setShowLeadCreateErrorAlert(true);
      }
      setShouldCreateLead(false);
    }
  }, [createLeadResponse]);

  useEffect(() => {
    if (!reviewing || !reviewRef.current) return;
    reviewRef.current.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  }, [reviewing]);

  return (
    <div ref={reviewRef}>
      <LeadCreationContext.Provider value={leadCreation}>
        <Box mt="sm" mb="sm" jc="fs" ai="center" gap="common">
          {reviewing ? (
            <>
              <BackButton
                variant="arrow"
                backButtonEnabled
                onClick={() => {
                  setReviewing(false);
                }}
              />
              <SubTitle subTitle={t('Review.pageTitle')} />
            </>
          ) : (
            <>
              {from === '/leads' && <BackButton variant="arrow" backButtonEnabled size="sm" />}
              <SubTitle subTitle={t('pageTitle')} />
            </>
          )}
        </Box>
        <LeadCreationForm defaultValues={defaultValues} handlers={handlers} reviewing={reviewing} />
        <LeadCreationDialogue handleDialogue={handlers.handleDialogue} />
      </LeadCreationContext.Provider>
    </div>
  );
};

export default LeadCreationContainer;
